/**
 * The per-family tested-asset cap, its one owner (wave H T3).
 *
 * A write is over the cap for a family when it leaves that family above the
 * registry's `maxPerStudy` AND above its count before the write: the net
 * change, so a draft already over stays editable and adding three then
 * removing two at the cap is no breach. Nest's applier refuses by it
 * (`asset_limit_exceeded`); PLAN's repair check (`asset_type_caps`) is held
 * to it by the generated cross-language table (`assetCapBreaches` cases).
 */
import { ASSET_FAMILY_FACTS, isAssetFamily } from "./asset-families";
import { TESTED_ASSET_TYPE_IDS } from "./registry";
import type { TestedAssetType } from "./index";
import { ruleImpl } from "./rule-impl";

/** Each tested-asset family's count among `assets` (entries of no family are skipped). */
export function testedAssetCountByType(assets: ReadonlyArray<unknown>): Map<string, number> {
  const counts = new Map<string, number>();
  for (const asset of assets) {
    if (!asset || typeof asset !== "object") continue;
    const type = (asset as { type?: unknown }).type;
    if (!isAssetFamily(type)) continue;
    counts.set(type, (counts.get(type) ?? 0) + 1);
  }
  return counts;
}

/**
 * Each family a write raises above its cap, as typed facts: the family, its
 * cap (the registry's `maxPerStudy`) and the count the write leaves.
 */
export function testedAssetCapBreaches(
  prevAssets: ReadonlyArray<unknown>,
  nextAssets: ReadonlyArray<unknown>,
): Array<{ family: TestedAssetType; limit: number; count: number; excess: number }> {
  const previous = testedAssetCountByType(prevAssets);
  const next = testedAssetCountByType(nextAssets);
  return TESTED_ASSET_TYPE_IDS.flatMap((family) => {
    const previousCount = previous.get(family) ?? 0;
    const count = next.get(family) ?? 0;
    const limit = ASSET_FAMILY_FACTS[family].maxPerStudy;
    return count > limit && count > previousCount
      ? [{ family, limit, count, excess: count - limit }]
      : [];
  });
}
ruleImpl("asset_limit_exceeded", testedAssetCapBreaches);
